import React, { useState } from "react";
import { toast } from "sonner";
import { Cast, Loader2, Tv, Copy } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, formatApiError } from "../../lib/api";

const canPresent = () => typeof window !== "undefined" && "PresentationRequest" in window;

export function CastButton({ mid, k, className = "" }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState(null);
  const [busy, setBusy] = useState(false);
  const [casting, setCasting] = useState(false);
  async function getCode() {
    if (code) return code;
    setBusy(true);
    try { const { data } = await api.post(`/public/meet/${mid}/tv-code`, { key: k }); setCode(data); return data; }
    catch (e) { toast.error(formatApiError(e)); return null; } finally { setBusy(false); }
  }
  async function openDialog() { setOpen(true); await getCode(); }
  async function chromecast() {
    const c = await getCode();
    if (!c) return;
    try {
      const req = new window.PresentationRequest([`${window.location.origin}${c.path}`]);
      const conn = await req.start();
      setCasting(true); toast.success("Casting to your TV");
      conn.onterminate = () => setCasting(false); conn.onclose = () => setCasting(false);
    } catch (e) { if (e?.name !== "AbortError" && e?.name !== "NotAllowedError") toast.error("No cast-ready TV found. Use the TV code instead."); }
  }
  const copy = async () => { await navigator.clipboard?.writeText(`${window.location.origin}${code.path}`); toast.success("TV link copied"); };
  return (
    <>
      <button onClick={openDialog} className={`${className} relative`} aria-label="Cast to TV" data-close-more="1" data-testid="meeting-cast-toolbar-btn">
        <Cast className={`w-5 h-5 ${casting ? "text-[#C9A25A]" : ""}`} />{casting && <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[#C9A25A] animate-ping" />}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-[#141414] text-white border border-[#C9A25A]/25" data-testid="cast-to-tv-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl font-light text-white">Show this meeting on a TV</DialogTitle></DialogHeader>
          {canPresent() && <button onClick={chromecast} className="btn-gold w-full inline-flex items-center justify-center gap-2" data-testid="cast-chromecast-btn"><Cast className="w-4 h-4" />Cast to Chromecast or smart TV</button>}
          <div className="rounded-2xl bg-white/5 border border-white/10 p-5 text-center">
            <p className="text-[10px] uppercase tracking-[0.24em] text-[#C9A25A]">TV code</p>
            {busy || !code ? <Loader2 className="w-6 h-6 animate-spin text-[#C9A25A] mx-auto my-4" /> : <p className="font-mono text-5xl tracking-[0.3em] my-3" data-testid="cast-pairing-code-display">{code.code}</p>}
            <p className="text-sm text-white/65">On the TV's web browser, go to <b className="text-white">lockedinforever.com/tv</b> and type this code.</p>
            {code && <button onClick={copy} className="mt-3 text-xs text-[#C9A25A] inline-flex items-center gap-1" data-testid="cast-copy-link"><Copy className="w-3 h-3" />Copy TV link</button>}
          </div>
          <ul className="text-sm text-white/70 space-y-2">
            <li className="flex gap-2"><Tv className="w-4 h-4 text-[#C9A25A] shrink-0 mt-0.5" /><span><b className="text-white">Apple TV / AirPlay:</b> open Control Center on your iPhone, iPad or Mac and tap Screen Mirroring.</span></li>
            <li className="flex gap-2"><Tv className="w-4 h-4 text-[#C9A25A] shrink-0 mt-0.5" /><span><b className="text-white">Chrome on a computer:</b> use the button above, or the ⋮ menu → Cast.</span></li>
            <li className="flex gap-2"><Tv className="w-4 h-4 text-[#C9A25A] shrink-0 mt-0.5" /><span>The TV joins as a view-only screen — it never uses a camera or mic. The code works for 12 hours.</span></li>
          </ul>
        </DialogContent>
      </Dialog>
    </>
  );
}
